import React from 'react';
import { Send, Plus, ChevronLeft, RotateCcw, Info } from 'lucide-react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '../ui/popover';

/**
 * iPhone 17 Pro Max chat mockup for the Test Agent panel.
 *
 * Renders an iMessage-style conversation so the test panel feels EXACTLY
 * like what an SMS/IG/FB visitor would see. Pacing (response_wait_time
 * delays, double-texting gaps) is driven entirely by the parent — this
 * component just renders the messages it's given plus a typing indicator
 * when `isProcessing` is true.
 *
 * Props:
 *   messages         : [{ role: 'user'|'assistant'|'error', content: string, functionCalls?: [] }]
 *   isProcessing     : bool — show typing dots
 *   value            : input value
 *   onChange         : (string) => void
 *   onSend           : () => void
 *   agentName        : display name in header
 *   testId           : optional data-testid for the root
 */
const IPhoneChatMockup = ({
  messages = [],
  isProcessing = false,
  value = '',
  onChange,
  onSend,
  onReset,
  agentName = 'AI Agent',
  testId = 'iphone-chat-mockup',
}) => {
  const messagesEndRef = React.useRef(null);
  React.useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isProcessing]);

  const initial = (agentName || 'A').trim().charAt(0).toUpperCase();
  const lastUserIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') return i;
    }
    return -1;
  })();

  return (
    <div className="relative w-full h-full flex items-center justify-center py-4 px-2" data-testid={testId}>
      {/* Subtle branded glow behind the phone */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 m-auto"
        style={{
          width: 480,
          height: 480,
          maxWidth: '90vw',
          maxHeight: '70vh',
          borderRadius: '50%',
          filter: 'blur(120px)',
          opacity: 0.55,
          background: `
            radial-gradient(circle at 30% 30%, #14b8a6 0%, transparent 50%),
            radial-gradient(circle at 70% 35%, #06b6d4 0%, transparent 55%),
            radial-gradient(circle at 60% 75%, #8b5cf6 0%, transparent 55%),
            radial-gradient(circle at 30% 70%, #ec4899 0%, transparent 50%)
          `,
          animation: 'iphoneGlowPulse 6s ease-in-out infinite',
        }}
      />

      {/* iPhone 17 Pro Max frame */}
      <div
        className="relative z-10 flex flex-col"
        style={{
          width: 420,
          height: 860,
          maxWidth: '95%',
          maxHeight: '95%',
          background: 'linear-gradient(180deg, #1f1f23 0%, #0a0a0c 100%)',
          borderRadius: 64,
          padding: 11,
          boxShadow:
            '0 0 0 2px #2c2c30, 0 0 0 4px #0a0a0a, 0 40px 80px rgba(0,0,0,0.5), 0 0 80px rgba(20,184,166,0.15)',
        }}
      >
        {/* Inner screen */}
        <div
          className="relative flex-1 flex flex-col overflow-hidden"
          style={{ background: '#000', borderRadius: 53 }}
        >
          {/* Dynamic Island (iPhone 17 — slightly larger pill) */}
          <div
            aria-hidden
            className="absolute z-20"
            style={{
              top: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 130,
              height: 36,
              background: '#000',
              borderRadius: 22,
            }}
          />

          {/* Status bar */}
          <div
            className="flex items-center justify-between px-8 text-white"
            style={{ height: 50, fontSize: 16, fontWeight: 600 }}
          >
            <span>9:41</span>
            <div className="flex items-center gap-1.5">
              {/* signal */}
              <div className="flex items-end gap-[2px]" style={{ height: 11 }}>
                <span className="w-[3px] bg-white rounded-[0.5px]" style={{ height: '25%' }} />
                <span className="w-[3px] bg-white rounded-[0.5px]" style={{ height: '50%' }} />
                <span className="w-[3px] bg-white rounded-[0.5px]" style={{ height: '75%' }} />
                <span className="w-[3px] bg-white rounded-[0.5px]" style={{ height: '100%' }} />
              </div>
              {/* 5G icon */}
              <span className="text-[10px] font-bold">5G</span>
              {/* battery */}
              <div
                className="relative flex items-center"
                style={{
                  width: 26,
                  height: 12,
                  border: '1.5px solid rgba(255,255,255,0.6)',
                  borderRadius: 4,
                  padding: 1.5,
                }}
              >
                <div className="absolute" style={{ right: -3, top: 3, width: 2, height: 4, background: 'rgba(255,255,255,0.6)', borderRadius: '0 1px 1px 0' }} />
                <div className="bg-white rounded-[1px]" style={{ width: '78%', height: '100%' }} />
              </div>
            </div>
          </div>

          {/* COMPACT iMessage header — back + avatar + name on left, video/phone on right */}
          <div
            className="flex items-center justify-between px-3"
            style={{
              height: 56,
              borderBottom: '0.5px solid rgba(255,255,255,0.1)',
              background: 'rgba(28,28,30,0.92)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <div className="flex items-center gap-2">
              <ChevronLeft size={26} className="text-[#0a84ff]" strokeWidth={2.5} />
              <div
                className="flex items-center justify-center text-white font-semibold relative"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #14b8a6, #06b6d4)',
                  fontSize: 13,
                }}
              >
                {initial}
                <span
                  className="absolute"
                  style={{
                    bottom: -1, right: -1,
                    width: 10, height: 10,
                    background: '#22c55e',
                    border: '2px solid #1c1c1e',
                    borderRadius: '50%',
                  }}
                />
              </div>
              <div className="flex flex-col">
                <span className="text-white font-semibold" style={{ fontSize: 13 }}>{agentName}</span>
                <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.45)' }}>active now</span>
              </div>
            </div>
            <div className="flex items-center gap-4 pr-2">
              {onReset && (
                <button
                  type="button"
                  onClick={() => {
                    if (messages.length === 0 || window.confirm('Restart this test conversation?')) {
                      onReset();
                    }
                  }}
                  className="text-[#0a84ff] transition-opacity hover:opacity-70"
                  title="Restart conversation"
                  data-testid="iphone-reset-btn"
                  aria-label="Restart conversation"
                >
                  <RotateCcw size={18} />
                </button>
              )}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-[#0a84ff] transition-opacity hover:opacity-70"
                    title="How chat testing works"
                    data-testid="iphone-info-btn"
                    aria-label="How chat testing works"
                  >
                    <Info size={18} />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="end"
                  className="w-80 text-sm"
                  data-testid="iphone-info-popover"
                >
                  <div className="space-y-2.5">
                    <div className="font-semibold text-base">How chat testing works</div>
                    <p className="text-muted-foreground leading-relaxed">
                      This is a preview environment for refining your agent's prompt — no real CRM,
                      calendar, or SMS calls are made.
                    </p>
                    <div className="space-y-1.5 pt-1">
                      <div className="flex gap-2">
                        <span className="text-[#0a84ff] mt-0.5">⚡</span>
                        <p className="leading-relaxed">
                          When you see a tool badge (e.g. <span className="font-mono text-xs px-1 rounded bg-muted">⚡ check_availability (simulated)</span>),
                          that's where the agent would call that tool in production. Here it's
                          simulated — the AI fabricates a plausible result and the conversation
                          flows so you can validate the logic.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <RotateCcw size={14} className="text-[#0a84ff] mt-1 shrink-0" />
                        <p className="leading-relaxed">
                          The refresh icon clears the conversation and starts a new test session.
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground pt-1 border-t">
                      Tweak the prompt on the left, restart the chat, and iterate until the flow
                      feels right — then your live SMS, widget, and voice channels will behave
                      identically.
                    </p>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Messages area */}
          <div
            className="flex-1 overflow-y-auto px-3 pt-3 pb-2"
            style={{ background: '#000', scrollbarWidth: 'thin' }}
          >
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center px-6">
                <div
                  className="flex items-center justify-center text-white font-semibold mb-3"
                  style={{
                    width: 60, height: 60, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #14b8a6, #06b6d4)',
                    fontSize: 22,
                  }}
                >
                  {initial}
                </div>
                <p className="text-white font-semibold text-sm mb-1">{agentName}</p>
                <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  Send a message to start the conversation
                </p>
              </div>
            )}

            {messages.map((msg, idx) => {
              if (msg.role === 'error') {
                return (
                  <div key={idx} className="my-2 mx-auto px-3 py-2 rounded-lg text-[11px] text-center max-w-[80%]"
                       style={{ background: 'rgba(244,63,94,0.12)', color: '#fda4af', border: '1px solid rgba(244,63,94,0.3)' }}>
                    {msg.content}
                  </div>
                );
              }
              const isUser = msg.role === 'user';
              const showDelivered = isUser && idx === lastUserIdx;
              return (
                <React.Fragment key={idx}>
                  {/* Function call badges (assistant only) */}
                  {!isUser && msg.functionCalls && msg.functionCalls.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1.5 ml-1" data-testid="iphone-tool-calls">
                      {msg.functionCalls.map((fc, i) => (
                        <span key={i}
                              className="text-[9px] px-1.5 py-0.5 rounded font-medium"
                              style={{ background: 'rgba(20,184,166,0.12)', color: '#5eead4', border: '1px solid rgba(20,184,166,0.3)' }}
                              title="Simulated — no real action performed (test panel only)"
                              data-testid={`iphone-tool-call-${fc.function || fc.name}`}>
                          ⚡ {fc.function || fc.name} <span style={{ opacity: 0.75 }}>(simulated)</span>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-[3px]`}>
                    <div
                      className="px-3 py-2 max-w-[75%] break-words"
                      style={{
                        fontSize: 15,
                        lineHeight: 1.35,
                        color: '#fff',
                        borderRadius: 20,
                        background: isUser
                          ? 'linear-gradient(135deg, #0fbfbf, #06b6d4)'
                          : '#2a2a2c',
                        borderBottomRightRadius: isUser ? 6 : 20,
                        borderBottomLeftRadius: isUser ? 20 : 6,
                        boxShadow: isUser ? '0 2px 10px rgba(20,184,166,0.25)' : 'none',
                      }}
                    >
                      {msg.content}
                    </div>
                  </div>
                  {showDelivered && (
                    <div className="text-right pr-2 mb-1" style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
                      Delivered
                    </div>
                  )}
                </React.Fragment>
              );
            })}

            {/* Typing indicator */}
            {isProcessing && (
              <div className="flex justify-start mb-1" data-testid="iphone-typing-indicator">
                <div
                  className="inline-flex items-center gap-1 px-4 py-3"
                  style={{
                    background: '#2a2a2c',
                    borderRadius: 20,
                    borderBottomLeftRadius: 6,
                  }}
                >
                  <span className="iphone-typing-dot" />
                  <span className="iphone-typing-dot" style={{ animationDelay: '0.2s' }} />
                  <span className="iphone-typing-dot" style={{ animationDelay: '0.4s' }} />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input bar */}
          <div
            className="flex items-center gap-2 px-3 pt-2 pb-6"
            style={{
              background: 'rgba(28,28,30,0.92)',
              backdropFilter: 'blur(20px)',
              borderTop: '0.5px solid rgba(255,255,255,0.08)',
            }}
          >
            <button
              type="button"
              aria-label="More"
              className="flex items-center justify-center shrink-0"
              style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', color: '#9ca3af' }}
            >
              <Plus size={18} />
            </button>
            <div className="relative flex-1">
              <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
                placeholder="iMessage"
                className="w-full text-white outline-none"
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 18,
                  padding: '7px 38px 7px 14px',
                  fontSize: 15,
                }}
                data-testid="iphone-chat-input"
              />
              <button
                type="button"
                onClick={onSend}
                disabled={!value.trim() || isProcessing}
                className="absolute flex items-center justify-center transition-opacity"
                style={{
                  right: 4,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: !value.trim()
                    ? 'rgba(255,255,255,0.08)'
                    : 'linear-gradient(135deg, #14b8a6, #06b6d4)',
                  boxShadow: value.trim() ? '0 2px 6px rgba(20,184,166,0.4)' : 'none',
                  opacity: isProcessing ? 0.5 : 1,
                }}
                data-testid="iphone-send-btn"
              >
                <Send size={14} className={value.trim() ? 'text-white' : 'text-white/40'} />
              </button>
            </div>
          </div>

          {/* Home indicator */}
          <div className="flex justify-center" style={{ padding: '0 0 8px' }}>
            <div style={{ width: 134, height: 5, background: 'rgba(255,255,255,0.35)', borderRadius: 3 }} />
          </div>
        </div>
      </div>

      <style>{`
        .iphone-typing-dot {
          width: 7px; height: 7px;
          background: rgba(255,255,255,0.55);
          border-radius: 50%;
          display: inline-block;
          animation: iphoneTypingBounce 1.4s infinite;
        }
        @keyframes iphoneTypingBounce {
          0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
          30% { opacity: 1; transform: translateY(-3px); }
        }
        @keyframes iphoneGlowPulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default IPhoneChatMockup;
